/**
 * EditableField — Sprint 2 shared primitive for auto-rendered approval cards.
 *
 * Renders a single editable form control whose type comes from the workflow
 * author's `content_schema_jsonb` (see packages/shared/src/schemas/
 * approval-content-schema.ts). One primitive, switch on field.type, lean on
 * the existing shadcn/ui controls so we don't introduce new dependencies.
 *
 * Authority: docs/STATEFUL-WORKFLOW-PLATFORM-SPEC.md §8.4 ("Each field
 * rendered by <EditableField type={type} />").
 *
 * Q9 reminder: `line_items` is NOT in the FieldType enum until Sprint 12.
 * If a schema slips one through, we render a JSON textarea fallback with a
 * warning — defense in depth, the Zod schema should have caught it upstream.
 */

import * as React from 'react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import type { FieldDef } from '@argusai/shared';

interface EditableFieldProps {
  field: FieldDef;
  value: unknown;
  onChange: (next: unknown) => void;
  readOnly?: boolean;
  /** Extra className applied to the wrapper. */
  className?: string;
}

function asString(v: unknown): string {
  if (v === null || v === undefined) return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return String(v);
  }
}

function asNumber(v: unknown): string {
  if (typeof v === 'number' && Number.isFinite(v)) return String(v);
  if (typeof v === 'string' && v.trim() !== '' && !Number.isNaN(Number(v))) {
    return v;
  }
  return '';
}

function asStringArray(v: unknown): string[] {
  if (Array.isArray(v)) return v.map((x) => String(x ?? ''));
  if (typeof v === 'string' && v.trim() !== '') {
    return v
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
  }
  return [];
}

export function EditableField({
  field,
  value,
  onChange,
  readOnly = false,
  className,
}: EditableFieldProps) {
  const id = `field-${field.path.replace(/\./g, '-')}`;
  const required = field.validation?.required ?? false;

  const labelEl = (
    <Label htmlFor={id} className="flex items-center gap-1 text-sm font-medium">
      <span>{field.label}</span>
      {required && (
        <span aria-label="required" className="text-destructive">
          *
        </span>
      )}
      {field.aiEditable && (
        <Badge
          variant="outline"
          className="ml-1 h-4 px-1 text-[9px] font-semibold uppercase tracking-wider"
          title="This field can be edited with AI"
        >
          AI
        </Badge>
      )}
    </Label>
  );

  const descriptionEl = field.description ? (
    <p className="text-xs text-muted-foreground">{field.description}</p>
  ) : null;

  let control: React.ReactNode;

  switch (field.type) {
    case 'text':
    case 'email':
    case 'phone':
    case 'url': {
      const inputType =
        field.type === 'email'
          ? 'email'
          : field.type === 'phone'
            ? 'tel'
            : field.type === 'url'
              ? 'url'
              : 'text';
      control = (
        <Input
          id={id}
          type={inputType}
          value={asString(value)}
          onChange={(e) => onChange(e.target.value)}
          readOnly={readOnly}
          disabled={readOnly}
          maxLength={field.validation?.maxLength}
          minLength={field.validation?.minLength}
          required={required}
        />
      );
      break;
    }

    case 'multiline':
    case 'rich_text': {
      // rich_text falls back to a tall textarea — we don't pull in a rich
      // editor dependency for Sprint 2 (the email_draft hand-designed
      // renderer is the place that wants WYSIWYG; the auto-renderer is
      // intentionally minimal).
      control = (
        <Textarea
          id={id}
          value={asString(value)}
          onChange={(e) => onChange(e.target.value)}
          readOnly={readOnly}
          disabled={readOnly}
          maxLength={field.validation?.maxLength}
          rows={field.type === 'rich_text' ? 12 : 5}
          required={required}
        />
      );
      break;
    }

    case 'number':
    case 'currency': {
      control = (
        <Input
          id={id}
          type="number"
          step={field.type === 'currency' ? '0.01' : 'any'}
          value={asNumber(value)}
          onChange={(e) => {
            const raw = e.target.value;
            if (raw === '') {
              onChange(null);
              return;
            }
            const num = Number(raw);
            onChange(Number.isFinite(num) ? num : raw);
          }}
          readOnly={readOnly}
          disabled={readOnly}
          min={field.validation?.min}
          max={field.validation?.max}
          required={required}
        />
      );
      break;
    }

    case 'date':
    case 'datetime': {
      const datetimeValue = (() => {
        const s = asString(value);
        if (!s) return '';
        // Trim seconds for datetime-local inputs ("YYYY-MM-DDTHH:mm")
        return field.type === 'datetime' ? s.slice(0, 16) : s.slice(0, 10);
      })();
      control = (
        <Input
          id={id}
          type={field.type === 'datetime' ? 'datetime-local' : 'date'}
          value={datetimeValue}
          onChange={(e) => onChange(e.target.value)}
          readOnly={readOnly}
          disabled={readOnly}
          required={required}
        />
      );
      break;
    }

    case 'checkbox': {
      control = (
        <Checkbox
          id={id}
          checked={value === true}
          onCheckedChange={(c) => onChange(c === true)}
          disabled={readOnly}
          aria-required={required || undefined}
        />
      );
      break;
    }

    case 'select': {
      control = (
        <Select
          value={asString(value)}
          onValueChange={(v) => onChange(v)}
          disabled={readOnly}
        >
          <SelectTrigger id={id} aria-required={required || undefined}>
            <SelectValue placeholder="Select…" />
          </SelectTrigger>
          <SelectContent>
            {(field.selectOptions ?? []).map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
      break;
    }

    case 'multiselect': {
      // Lightweight checkbox-list rendering — keeps Sprint 2 dependency-free.
      const selected = new Set(asStringArray(value));
      const toggle = (val: string) => {
        const next = new Set(selected);
        if (next.has(val)) next.delete(val);
        else next.add(val);
        onChange(Array.from(next));
      };
      control = (
        <div
          id={id}
          role="group"
          aria-required={required || undefined}
          className="flex flex-col gap-1 rounded-md border border-input bg-background p-2"
        >
          {(field.selectOptions ?? []).map((opt) => {
            const checkId = `${id}-${opt.value}`;
            return (
              <label
                key={opt.value}
                htmlFor={checkId}
                className="flex items-center gap-2 text-sm"
              >
                <Checkbox
                  id={checkId}
                  checked={selected.has(opt.value)}
                  onCheckedChange={() => toggle(opt.value)}
                  disabled={readOnly}
                />
                <span>{opt.label}</span>
              </label>
            );
          })}
          {(!field.selectOptions || field.selectOptions.length === 0) && (
            <span className="text-xs text-muted-foreground">
              No options configured
            </span>
          )}
        </div>
      );
      break;
    }

    case 'recipients': {
      // Comma-separated recipient list. The hand-designed email_draft
      // renderer can replace this with a real recipient picker.
      const recipientStr = asStringArray(value).join(', ');
      control = (
        <Input
          id={id}
          type="text"
          inputMode="email"
          value={recipientStr}
          onChange={(e) => onChange(asStringArray(e.target.value))}
          readOnly={readOnly}
          disabled={readOnly}
          placeholder="comma-separated email addresses"
          required={required}
        />
      );
      break;
    }

    case 'classification': {
      // Two-axis: sensitivity + exportControl. Render as a JSON viewer
      // for Sprint 2 (auto-renderer); the governance UI in a later sprint
      // can swap in a richer chip-based control.
      control = (
        <Textarea
          id={id}
          value={asString(value)}
          onChange={(e) => {
            const raw = e.target.value;
            try {
              onChange(JSON.parse(raw));
            } catch {
              onChange(raw);
            }
          }}
          readOnly={readOnly}
          disabled={readOnly}
          rows={3}
          className="font-mono text-xs"
        />
      );
      break;
    }

    case 'json':
    default: {
      control = (
        <Textarea
          id={id}
          value={asString(value)}
          onChange={(e) => {
            const raw = e.target.value;
            try {
              onChange(JSON.parse(raw));
            } catch {
              onChange(raw);
            }
          }}
          readOnly={readOnly}
          disabled={readOnly}
          rows={6}
          className="font-mono text-xs"
        />
      );
      break;
    }
  }

  return (
    <div
      className={cn('flex flex-col gap-1.5', className)}
      data-field-path={field.path}
      data-field-type={field.type}
      data-ai-editable={field.aiEditable ? 'true' : 'false'}
    >
      {labelEl}
      {control}
      {descriptionEl}
    </div>
  );
}
